import React, { useEffect, useState, useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  getTreatmentPieChart,
  TreatmentPieChartResponse,
} from "../../../api/dashboardService";
import PieChartIcon from "@mui/icons-material/PieChart";

type TreatmentChartItem = TreatmentPieChartResponse["data"][number];

type ChartSlice = TreatmentChartItem & {
  id: string | number;
  percentage: string;
  isOther?: boolean;
};

const TOP_N = 6;
const OTHER_COLOR = "#94a3b8";
const SLICE_COLORS = [
  "#10b981",
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
];

function withColors(items: TreatmentChartItem[]): TreatmentChartItem[] {
  return items.map((item, index) => ({
    ...item,
    color: item.color || SLICE_COLORS[index % SLICE_COLORS.length],
  }));
}

/** Top N by count; everything else rolls into a single Other slice. */
function groupTopNPlusOther(
  items: TreatmentChartItem[],
  topN = TOP_N
): { slices: ChartSlice[]; otherItems: TreatmentChartItem[]; total: number } {
  const sorted = [...items]
    .filter((item) => (item.value || 0) > 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  const total = sorted.reduce((sum, item) => sum + (item.value || 0), 0);
  const pct = (value: number) =>
    total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

  if (sorted.length <= topN) {
    return {
      slices: withColors(sorted).map((item, index) => ({
        ...item,
        id: item.label || index,
        percentage: pct(item.value || 0),
      })),
      otherItems: [],
      total,
    };
  }

  const top = withColors(sorted.slice(0, topN));
  const rest = sorted.slice(topN);
  const otherValue = rest.reduce((sum, item) => sum + (item.value || 0), 0);

  return {
    slices: [
      ...top.map((item, index) => ({
        ...item,
        id: item.label || index,
        percentage: pct(item.value || 0),
      })),
      {
        id: "other",
        label: `Other (${rest.length})`,
        value: otherValue,
        color: OTHER_COLOR,
        percentage: pct(otherValue),
        isOther: true,
      },
    ],
    otherItems: rest,
    total,
  };
}

const TreatmentPieChart = React.memo(() => {
  const [rawChart, setRawChart] = useState<TreatmentChartItem[]>([]);
  const [chartData, setChartData] = useState<{
    month: TreatmentChartItem[];
    week: TreatmentChartItem[];
  }>({ month: [], week: [] });
  const [select, setSelect] = useState<"month" | "week">("month");
  const [showOtherDetails, setShowOtherDetails] = useState(false);

  useEffect(() => {
    getTreatmentPieChart()
      .then((data) => {
        const month = data.data ?? [];
        const week = data.treatmenttoday ?? [];
        setChartData({ month, week });
        setRawChart(month);
      })
      .catch((error) => {
        console.error(error.response);
      });
  }, []);

  const handleChange = (value: "month" | "week") => {
    setSelect(value);
    setShowOtherDetails(false);
    setRawChart(value === "month" ? chartData.month : chartData.week);
  };

  const { slices, otherItems, total } = useMemo(
    () => groupTopNPlusOther(rawChart, TOP_N),
    [rawChart]
  );

  const otherWithPercentage = useMemo(() => {
    if (!otherItems.length || total <= 0) return [];
    return otherItems.map((item) => ({
      ...item,
      percentage: (((item.value || 0) / total) * 100).toFixed(1),
    }));
  }, [otherItems, total]);

  return (
    <div className="rounded-2xl shadow-lg border overflow-hidden h-full transition-all duration-300 bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
      <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <PieChartIcon className="text-white" sx={{ fontSize: 20 }} />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
            Treatment Distribution
          </h3>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
          <button
            type="button"
            onClick={() => handleChange("month")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "month"
                ? "bg-white text-emerald-600 shadow-sm dark:bg-slate-600 dark:text-emerald-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Month
          </button>
          <button
            type="button"
            onClick={() => handleChange("week")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "week"
                ? "bg-white text-emerald-600 shadow-sm dark:bg-slate-600 dark:text-emerald-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Week
          </button>
        </div>
      </div>

      <div className="p-4">
        {slices.length === 0 ? (
          <div className="py-16 text-center text-slate-400 dark:text-slate-500">
            <PieChartIcon sx={{ fontSize: 48 }} className="mb-2" />
            <p>No treatment data available</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <PieChart
                series={[
                  {
                    data: slices.map(({ id, value, label, color }) => ({
                      id,
                      value,
                      label,
                      color,
                    })),
                    innerRadius: 40,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                width={250}
                height={200}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
            </div>

            <div className="text-center py-2 border-b border-slate-200 dark:border-slate-700">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Total Treatments:{" "}
              </span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {total}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {slices.map((item) => {
                const isOther = Boolean(item.isOther);
                return (
                  <button
                    key={String(item.id)}
                    type="button"
                    disabled={!isOther || otherItems.length === 0}
                    onClick={() => {
                      if (isOther) setShowOtherDetails((open) => !open);
                    }}
                    className={`p-2 rounded-lg transition-colors text-center bg-slate-50 dark:bg-slate-700/50 ${
                      isOther && otherItems.length > 0
                        ? "hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer ring-1 ring-transparent hover:ring-slate-200 dark:hover:ring-slate-600"
                        : "cursor-default"
                    } ${
                      isOther && showOtherDetails
                        ? "ring-slate-300 dark:ring-slate-500 bg-slate-100 dark:bg-slate-700"
                        : ""
                    }`}
                    title={
                      isOther && otherItems.length > 0
                        ? showOtherDetails
                          ? "Hide other treatments"
                          : "Show other treatments"
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: item.color || OTHER_COLOR,
                        }}
                      />
                      <span className="text-xs font-medium truncate text-slate-700 dark:text-slate-300">
                        {item.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                        {item.value}
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border dark:border-emerald-800/50">
                        {item.percentage}%
                      </span>
                    </div>
                    {isOther && otherItems.length > 0 ? (
                      <span className="mt-1 block text-[10px] text-slate-400 dark:text-slate-500">
                        {showOtherDetails ? "Hide list" : "Tap to expand"}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {showOtherDetails && otherWithPercentage.length > 0 ? (
              <div className="rounded-lg border border-slate-200 dark:border-slate-600 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Other treatments ({otherWithPercentage.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowOtherDetails(false)}
                    className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    Close
                  </button>
                </div>
                <div className="max-h-[220px] overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {otherWithPercentage.map((item, index) => (
                      <div
                        key={`${item.label}-${index}`}
                        className="p-2 rounded-lg text-center bg-slate-50 dark:bg-slate-700/40"
                      >
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: OTHER_COLOR }}
                          />
                          <span className="text-xs font-medium truncate text-slate-700 dark:text-slate-300">
                            {item.label}
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {item.value}
                          </span>
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-300">
                            {item.percentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
});

export default TreatmentPieChart;
